import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import "../Styles/DateSessionSelector.css";
import { Colors } from "../Helper/themes.jsx";
import axios from "axios";
import { WiDaySunny } from "react-icons/wi";
import { IoMoonOutline } from "react-icons/io5";

const dateToKeyUTC = (date) => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const sessionConfig = {
  Breakfast: {
    icon: WiDaySunny,
    deliveryTime: "7-8 AM",
    displayName: "Breakfast",
  },
  Lunch: { icon: WiDaySunny, deliveryTime: "12-1 PM", displayName: "Lunch" },
  Dinner: {
    icon: IoMoonOutline,
    deliveryTime: "7-8 PM",
    displayName: "Dinner",
  },
};

// Helper function to check if a session is past cutoff
const isSessionPastCutoff = (session, currentTime, hubData, isEmployee) => {
  if (!hubData?.cutoffTimes) return false;

  const sessionKey = session.toLowerCase();
  const cutoffTimes = hubData.cutoffTimes[sessionKey];

  if (!cutoffTimes) return false;

  // Get appropriate cutoff time based on user type
  const cutoffTimeStr = isEmployee
    ? cutoffTimes.employeeCutoff
    : cutoffTimes.defaultCutoff;

  if (!cutoffTimeStr) return false;

  // Parse cutoff time (format: "HH:MM")
  const [cutoffHour, cutoffMinute] = cutoffTimeStr.split(":").map(Number);

  const cutoffDate = new Date(currentTime);
  cutoffDate.setHours(cutoffHour, cutoffMinute, 0, 0);

  // Session is past cutoff if current time is after cutoff time
  return currentTime > cutoffDate;
};

const DateSessionSelector = ({
  onChange,
  currentDate,
  currentSession,
  menuData = [],
  hubCutoffData = null, // passed from Home — avoids duplicate fetch
  menuLoading = false, // true while Home is still fetching allHubMenuData
}) => {
  const getUser = () => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  };

  const user = getUser();
  const isEmployee = user?.status === "Employee";

  // Use prop if provided, otherwise fall back to internal fetch
  const [hubDataInternal, setHubDataInternal] = useState(null);
  const hubData = hubCutoffData || hubDataInternal;

  const [currentHubId, setCurrentHubId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  const sessionOrder = ["Breakfast", "Lunch", "Dinner"];

  // Update time every second for precise cutoff checking
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getHubIdFromAddress = () => {
    try {
      const primaryAddress = localStorage.getItem("primaryAddress");
      const currentLocation = localStorage.getItem("currentLocation");
      if (primaryAddress && primaryAddress !== "null") {
        const parsed = JSON.parse(primaryAddress);
        return parsed.hubId;
      }
      if (currentLocation && currentLocation !== "null") {
        const parsed = JSON.parse(currentLocation);
        return parsed.hubId;
      }
    } catch (error) {
      console.error("Error getting hub ID:", error);
    }
    return null;
  };

  const fetchHubData = async (hubId) => {
    if (!hubId) return null;
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(
        `https://dailydish.in/api/Hub/get-cutoff-times/${hubId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.status === 200) {
        // console.log("Hub data fetched:", res.data);

        const cutoffTimes = res.data.cutoffTimes || {};
        const orderMode = res.data.orderMode || "preorder";

        ["breakfast", "lunch", "dinner"].forEach((session) => {
          if (!cutoffTimes[session]) {
            cutoffTimes[session] = {};
          }
          if (!cutoffTimes[session].defaultCutoff) {
            cutoffTimes[session].defaultCutoff =
              orderMode === "instant" ? "20:00" : "23:59";
          }
          if (!cutoffTimes[session].employeeCutoff) {
            cutoffTimes[session].employeeCutoff = "10:00";
          }
        });

        return { orderMode, cutoffTimes };
      }
    } catch (error) {
      console.error("Error fetching hub data:", error);
      return {
        orderMode: "preorder",
        cutoffTimes: {
          breakfast: { defaultCutoff: "23:59", employeeCutoff: "10:00" },
          lunch: { defaultCutoff: "23:59", employeeCutoff: "10:00" },
          dinner: { defaultCutoff: "23:59", employeeCutoff: "10:00" },
        },
      };
    } finally {
      setLoading(false);
    }
    return null;
  };

  useEffect(() => {
    const handleLocationChange = async () => {
      // If parent is providing hubCutoffData, skip internal fetch entirely
      if (hubCutoffData) return;

      const hubId = getHubIdFromAddress();
      if (hubId) {
        if (hubId !== currentHubId) {
          console.log("Hub changed to:", hubId);
          setCurrentHubId(hubId);
          const data = await fetchHubData(hubId);
          if (data) {
            setHubDataInternal(data);
          }
        }
      }
    };

    handleLocationChange();

    window.addEventListener("locationUpdated", handleLocationChange);
    window.addEventListener("addressUpdated", handleLocationChange);
    window.addEventListener("storage", handleLocationChange);

    return () => {
      window.removeEventListener("locationUpdated", handleLocationChange);
      window.removeEventListener("addressUpdated", handleLocationChange);
      window.removeEventListener("storage", handleLocationChange);
    };
  }, [currentHubId, hubCutoffData]);

  const availability = useMemo(() => {
    const map = {};
    (menuData || []).forEach((item) => {
      if (!item?.deliveryDate || !item?.session) return;
      const d = new Date(item.deliveryDate);
      const keyUtc = dateToKeyUTC(d);
      if (!map[keyUtc]) map[keyUtc] = new Set();
      map[keyUtc].add(item.session);
    });
    return map;
  }, [menuData]);

  const isSessionAvailable = useCallback(
    (dateObj, session) => {
      if (!dateObj) return false;
      const key = dateToKeyUTC(dateObj);
      const availableSessions = availability[key] || new Set();

      // Check if menu has this session
      if (!availableSessions.has(session)) {
        return false;
      }

      // Check cutoff for today's date
      const now = currentTime;
      const dateKey = dateToKeyUTC(dateObj);
      const todayDateKey = dateToKeyUTC(now);

      if (dateKey === todayDateKey) {
        const isPastCutoff = isSessionPastCutoff(
          session,
          now,
          hubData,
          isEmployee,
        );
        if (isPastCutoff) {
          return false;
        }
      }

      return true;
    },
    [availability, currentTime, hubData, isEmployee],
  );

  const sessionsForDate = useCallback(
    (dateObj) => {
      if (!dateObj) return new Set();
      const key = dateToKeyUTC(dateObj);
      const menuSessions = availability[key] || new Set();
      const result = new Set();
      menuSessions.forEach((session) => {
        if (isSessionAvailable(dateObj, session)) {
          result.add(session);
        }
      });
      return result;
    },
    [availability, isSessionAvailable],
  );

  // Home.jsx owns the selected date/session state and handles all auto-selection logic.
  // DateSessionSelector is a controlled component: it only reflects `currentDate`
  // (normalized to UTC midnight) and reports user clicks via onChange.
  const displayDate = useMemo(() => {
    const base = currentDate || currentTime;
    return {
      dateObj: new Date(
        Date.UTC(base.getFullYear(), base.getMonth(), base.getDate()),
      ),
    };
  }, [currentDate, currentTime]);

  const handleSessionClick = (session) => {
    if (!displayDate?.dateObj) return;
    const sessions = sessionsForDate(displayDate.dateObj);
    if (!sessions.has(session)) return;
    onChange(displayDate.dateObj, session);
  };

  const isSessionDisabled = (session) => {
    if (!displayDate?.dateObj) return true;
    return !sessionsForDate(displayDate.dateObj).has(session);
  };

  // True if this session has no menu items at all for the selected date
  // (distinct from cutoff-expired which hides the card entirely)
  const isSessionComingSoon = (session) => {
    if (!displayDate?.dateObj) return false;
    const key = dateToKeyUTC(displayDate.dateObj);
    const menuSessions = availability[key] || new Set();
    // No menu entry for this session on this date → Coming Soon
    return !menuSessions.has(session);
  };

  // True if the session's cutoff has already expired (today only)
  const isSessionCutoffExpired = (session) => {
    if (!displayDate?.dateObj) return false;
    const dateKey = dateToKeyUTC(displayDate.dateObj);
    const todayKey = dateToKeyUTC(currentTime);
    if (dateKey !== todayKey) return false;
    return isSessionPastCutoff(session, currentTime, hubData, isEmployee);
  };

  // True if the session is currently in its post-cutoff window (today only)
  const isSessionInWindow = (session) => {
    if (!displayDate?.dateObj) return false;
    const dateKey = dateToKeyUTC(displayDate.dateObj);
    const todayKey = dateToKeyUTC(currentTime);
    if (dateKey !== todayKey) return false;
    const key = session.toLowerCase();
    const times = hubData?.cutoffTimes?.[key];
    if (!times?.windowEnabled || !times?.windowEnd) return false;
    const IST_MS = 5.5 * 60 * 60 * 1000;
    const ist = new Date(currentTime.getTime() + IST_MS);
    const nowMins = ist.getUTCHours() * 60 + ist.getUTCMinutes();
    const [ch, cm] = (times.defaultCutoff || "0:0").split(":").map(Number);
    const [wh, wm] = times.windowEnd.split(":").map(Number);
    return nowMins >= ch * 60 + cm && nowMins < wh * 60 + wm;
  };

  const getSessionIcon = (session) => sessionConfig[session]?.icon;
  const getDeliveryTime = (session) =>
    sessionConfig[session]?.deliveryTime || "";

  const formatCutoffTime = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour = h % 12 || 12;
    return `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
  };

  const getCutoffTimeForSession = (session) => {
    const key = session.toLowerCase();
    const times = hubData?.cutoffTimes?.[key];
    const raw = isEmployee ? times?.employeeCutoff : times?.defaultCutoff;
    return formatCutoffTime(raw);
  };

  const getOrderModeInfo = () => {
    const orderMode = hubData?.orderMode || "preorder";
    if (isEmployee) {
      return { badge: "👔 Employee", description: "Same-day ordering" };
    } else if (orderMode === "instant") {
      return { badge: "⚡ Instant", description: "Same-day delivery" };
    } else {
      return { badge: "📋 Preorder", description: "Order for tomorrow" };
    }
  };

  if (loading || menuLoading) {
    return (
      <div className="date-session-wrapper">
        <div
          className="loading-placeholder"
          style={{ textAlign: "center", padding: "20px" }}
        >
          Loading availability...
        </div>
      </div>
    );
  }

  const allUnavailable = sessionOrder.every(
    (s) =>
      (isSessionComingSoon(s) || isSessionCutoffExpired(s)) &&
      !isSessionInWindow(s),
  );

  return (
    <div className="date-session-wrapper">
      <div className="session-container">
        {sessionOrder.map((session) => {
          const Icon = getSessionIcon(session);
          const isActive = currentSession === session;
          const deliveryTime = getDeliveryTime(session);
          const cutoffExpired = isSessionCutoffExpired(session);
          const comingSoon = isSessionComingSoon(session);
          const isDisabled = isSessionDisabled(session);

          // Menu added but cutoff passed — check if window is open first
          if (!comingSoon && cutoffExpired) {
            if (isSessionInWindow(session)) {
              return (
                <div
                  key={session}
                  className={`session-card ${isActive ? "active" : ""}`}
                  onClick={() => onChange(displayDate.dateObj, session)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="session-icon">
                    {Icon && (
                      <Icon size={20} color={isActive ? "#fff" : "#FF9800"} />
                    )}
                  </div>
                  <div className="session-info">
                    <div className="session-title">
                      {sessionConfig[session].displayName}
                    </div>
                    <div
                      style={{
                        fontSize: "10px",
                        fontWeight: 700,
                        color: isActive ? "rgba(255,255,255,0.9)" : "#FF9800",
                        marginTop: "2px",
                      }}
                    >
                      🔥 Window Open
                    </div>
                    <div
                      className="session-time"
                      style={{
                        fontSize: "10px",
                        color: isActive ? "rgba(255,255,255,0.8)" : "#888",
                      }}
                    >
                      {deliveryTime}
                    </div>
                  </div>
                </div>
              );
            }
            const cutoffDisplay = getCutoffTimeForSession(session);
            return (
              <div
                key={session}
                className="session-card session-card--unavailable"
                title="Ordering closed for this session"
              >
                <div className="session-icon session-icon--muted">
                  <Icon size={20} />
                </div>
                <div className="session-info">
                  <div className="session-title session-title--muted">
                    {sessionConfig[session].displayName}
                  </div>
                  <div className="session-unavail-badge">Closed</div>
                  <div className="session-unavail-text">
                    {cutoffDisplay
                      ? `Next: ${cutoffDisplay}`
                      : "Menu drops soon!"}
                  </div>
                </div>
              </div>
            );
          }

          // No menu for this session on the selected date → show Serving Soon
          if (comingSoon) {
            return (
              <div
                key={session}
                className="session-card session-card--unavailable"
                title="Menu not available yet for this session"
              >
                <div className="session-icon session-icon--muted">
                  <Icon size={24} />
                </div>
                <div className="session-info">
                  <div className="session-title session-title--muted">
                    {sessionConfig[session].displayName}
                  </div>
                  <div className="session-unavail-badge">Serving Soon</div>
                  <div className="session-unavail-text">Menu drops soon!</div>
                </div>
              </div>
            );
          }

          return (
            <div
              key={session}
              className={`session-card ${isActive ? "active" : ""} ${isDisabled ? "disabled" : ""}`}
              onClick={() => !isDisabled && handleSessionClick(session)}
              title={isDisabled ? "Ordering closed for this session" : ""}
              style={{
                cursor: isDisabled ? "not-allowed" : "pointer",
                opacity: isDisabled ? 0.5 : 1,
                transition: "all 0.3s ease",
              }}
            >
              <div className="session-icon">
                <Icon size={24} />
              </div>
              <div className="session-info">
                <div className="session-title">
                  {sessionConfig[session].displayName}
                </div>
                <div className="session-delivered-by">Delivered by</div>
                {isActive ? (
                  <div className="subtext2 active">{deliveryTime}</div>
                ) : (
                  <div className="session-delivery-time-plain">
                    {deliveryTime}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {allUnavailable && (
        <div className="no-session-banner">
          <div className="no-session-icon">🌙</div>
          <div className="no-session-title">No ordering right now</div>
          <div className="no-session-sub">
            All session windows are closed for today.
            <br />
            Come back tomorrow to place your order!
          </div>
        </div>
      )}
    </div>
  );
};

export default DateSessionSelector;
